import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, UserPlus, Trash2, Clock, CheckCircle2, Crown, Users, Copy, Link, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import { usePetMembers } from '../hooks/usePetMembers';
import { usePet } from '../context/PetContext';
import { useLanguage } from '../functions/useLanguage';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

function formatDate(iso: string) {
  try { return format(new Date(iso), 'd MMM yyyy'); } catch { return ''; }
}

export default function CoCarers({ embedded = false }: { embedded?: boolean }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { activePet } = usePet();
  const {
    members,
    invites,
    loading,
    isOwner,
    seatLimit,
    isHandoffMode,
    inviteCarer,
    transferOwnership,
    removeCarer,
    cancelInvite,
    buildInviteUrl,
  } = usePetMembers();

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null); // invite id or 'new'
  const [removingUid, setRemovingUid] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferSuccess, setTransferSuccess] = useState('');
  const [showTransferSection, setShowTransferSection] = useState(false);

  const handleCreateLink = async () => {
    setCreating(true);
    setCreateError('');
    try {
      const url = await inviteCarer();
      await navigator.clipboard.writeText(url);
      setCopiedId('new');
      setTimeout(() => setCopiedId(null), 4000);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : t('members.inviteError');
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async (token: string, inviteId: string) => {
    const url = buildInviteUrl(token, inviteId);
    await navigator.clipboard.writeText(url);
    setCopiedId(inviteId);
    setTimeout(() => setCopiedId(null), 3000);
  };

  const handleRemove = async (uid: string, name: string) => {
    if (!confirm(t('members.confirmRemove').replace('{name}', name))) return;
    setRemovingUid(uid);
    try { await removeCarer(uid); }
    catch { /* silently ignore */ }
    finally { setRemovingUid(null); }
  };

  const handleCancelInvite = async (inviteId: string) => {
    setCancellingId(inviteId);
    try { await cancelInvite(inviteId); }
    catch { /* silently ignore */ }
    finally { setCancellingId(null); }
  };

  const handleTransferOwnership = async () => {
    const email = transferEmail.trim();
    if (!email) return;
    const confirmMsg = t('members.transferConfirm')
      .replace('{petName}', activePet?.name ?? 'this pet')
      .replace('{email}', email);
    if (!confirm(confirmMsg)) return;

    setTransferring(true);
    setTransferError('');
    setTransferSuccess('');
    try {
      await transferOwnership(email);
      setTransferSuccess(t('members.transferSuccess'));
      setTransferEmail('');
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : t('members.transferError');
      setTransferError(message);
    } finally {
      setTransferring(false);
    }
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const coCarers = members.filter(m => m.role === 'carer');
  const ownerMember = members.find(m => m.role === 'owner');

  return (
    <div className={embedded ? 'w-full' : 'w-full max-w-lg mx-auto'}>
      {/* Header — only shown when rendered as a standalone page */}
      {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-full hover:bg-gray-100 text-primary-brown transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
          <div>
            <h1 className="text-xl font-black text-[#0d2744]">{t('members.title')}</h1>
            {activePet && <p className="text-sm text-gray-500">{activePet.name}</p>}
          </div>
        </div>
      )}

      {!isOwner && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-sm text-amber-800">
          {t('members.viewOnlyNotice')}
        </div>
      )}

      {/* Handoff flow for pet-sitter accounts */}
      {isOwner && isHandoffMode && (
        <section className="bg-white border border-slate-100 rounded-3xl p-6 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={18} className="text-primary-brown" />
            <h2 className="text-base font-bold text-[#0d2744]">Ownership handoff</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Pet sitter accounts have no co-carer seats. Invite the owner by email to transfer this pet profile.
            You will automatically remain connected as a subordinate account after transfer.
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              placeholder="owner@email.com"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none focus:ring-2 focus:ring-primary-brown/30"
            />
            <button
              onClick={handleTransferOwnership}
              disabled={transferring || !transferEmail.trim()}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-primary-brown text-white hover:bg-primary-brown/90 disabled:opacity-50"
            >
              {transferring ? 'Transferring…' : 'Transfer ownership'}
            </button>
          </div>

          {transferError && <p className="text-xs text-error-red mt-2">{transferError}</p>}
          {transferSuccess && <p className="text-xs text-green-700 mt-2">{transferSuccess}</p>}
        </section>
      )}

      {/* Invite via share link (standard accounts with seats) */}
      {isOwner && !isHandoffMode && (
        <section className="bg-white border border-slate-100 rounded-3xl p-6 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={18} className="text-primary-brown" />
            <h2 className="text-base font-bold text-[#0d2744]">{t('members.inviteTitle')}</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t('members.inviteHint')}</p>
          <p className="text-xs text-gray-400 mb-3">Co-carer seats available: {seatLimit}</p>

          <button
            onClick={handleCreateLink}
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 py-3 bg-primary-brown text-white rounded-2xl text-sm font-semibold disabled:opacity-40 hover:bg-primary-brown/90 transition-colors"
          >
            <Link size={15} />
            {creating ? '…' : t('members.createLink')}
          </button>

          {createError && <p className="text-xs text-error-red mt-2">{createError}</p>}
          {copiedId === 'new' && (
            <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
              <CheckCircle2 size={13} />
              {t('members.linkCopied')}
            </p>
          )}
        </section>
      )}

      {/* Current members */}
      <section className="bg-white border border-slate-100 rounded-3xl p-6 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} className="text-primary-brown" />
          <h2 className="text-base font-bold text-[#0d2744]">{t('members.membersTitle')}</h2>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400">{t('members.loading')}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-gray-400">{t('members.noMembers')}</p>
        ) : (
          <ul className="space-y-3">
            {/* Owner first */}
            {ownerMember && (
              <li className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-brown/10 flex items-center justify-center text-sm font-black text-primary-brown">
                    {ownerMember.display_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0d2744]">{ownerMember.display_name}</p>
                    <p className="text-xs text-gray-400">{ownerMember.email}</p>
                  </div>
                </div>
                <span className="flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <Crown size={11} />
                  {t('members.owner')}
                </span>
              </li>
            )}
            {/* Co-carers */}
            {coCarers.map(member => (
              <li key={member.uid} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-black text-gray-500">
                    {member.display_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0d2744]">{member.display_name}</p>
                    <p className="text-xs text-gray-400">{member.email}</p>
                    <p className="text-xs text-gray-300">{t('members.joinedOn')} {formatDate(member.joined_at)}</p>
                  </div>
                </div>
                {isOwner && (
                  <button
                    onClick={() => handleRemove(member.uid, member.display_name)}
                    disabled={removingUid === member.uid}
                    className="p-2 rounded-full text-error-red\/60 hover:bg-error-red\/5 transition-colors disabled:opacity-40"
                    title={t('members.remove')}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pending invites */}
      {isOwner && !isHandoffMode && pendingInvites.length > 0 && (
        <section className="bg-white border border-slate-100 rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={18} className="text-primary-brown" />
            <h2 className="text-base font-bold text-[#0d2744]">{t('members.pendingTitle')}</h2>
          </div>
          <ul className="space-y-3">
            {pendingInvites.map(invite => (
              <li key={invite.id} className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-[#0d2744]">
                    {invite.email ?? 'Invite link'}
                  </p>
                  <p className="text-xs text-gray-400">{t('members.sentOn')} {formatDate(invite.created_at)}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleCopyLink(invite.token, invite.id)}
                    className={cn(
                      'flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors',
                      copiedId === invite.id
                        ? 'text-green-700 bg-green-50'
                        : 'text-primary-brown bg-primary-brown/10 hover:bg-primary-brown/20'
                    )}
                  >
                    <Copy size={12} />
                    {copiedId === invite.id ? t('members.copied') : t('members.copyLink')}
                  </button>
                  <button
                    onClick={() => handleCancelInvite(invite.id)}
                    disabled={cancellingId === invite.id}
                    className="text-xs font-semibold px-3 py-1.5 rounded-xl text-gray-400 hover:bg-error-red\/5 hover:text-error-red transition-colors disabled:opacity-40"
                  >
                    {cancellingId === invite.id ? '…' : t('members.cancelInvite')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Transfer Ownership — available to all non-handoff owners */}
      {isOwner && !isHandoffMode && (
        <section className="bg-white border border-slate-100 rounded-3xl p-6 mt-5">
          <button
            type="button"
            onClick={() => setShowTransferSection(v => !v)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-2">
              <ArrowRightLeft size={17} className="text-red-400" />
              <h2 className="text-base font-bold text-[#0d2744]">{t('members.transferTitle')}</h2>
            </div>
            <ChevronDown
              size={16}
              className={cn('text-gray-400 transition-transform duration-200', showTransferSection && 'rotate-180')}
            />
          </button>

          {showTransferSection && (
            <div className="mt-4">
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-2xl mb-4">
                <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{t('members.transferWarning')}</p>
              </div>

              <p className="text-sm text-gray-500 mb-4">{t('members.transferHint').replace('{petName}', activePet?.name ?? 'this pet')}</p>

              {coCarers.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-400 mb-2">{t('members.transferSelectCarer')}</p>
                  <div className="flex flex-wrap gap-2">
                    {coCarers.map(c => (
                      <button
                        key={c.uid}
                        type="button"
                        onClick={() => setTransferEmail(c.email)}
                        className={cn(
                          'text-xs px-3 py-1.5 rounded-full border font-medium transition-colors',
                          transferEmail === c.email
                            ? 'bg-primary-brown text-white border-primary-brown'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-primary-brown/50 hover:text-primary-brown'
                        )}
                      >
                        {c.display_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={transferEmail}
                  onChange={e => setTransferEmail(e.target.value)}
                  placeholder={t('members.transferEmail')}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[#0d2744] font-medium outline-none focus:ring-2 focus:ring-red-300/30"
                />
                <button
                  type="button"
                  onClick={handleTransferOwnership}
                  disabled={transferring || !transferEmail.trim()}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors whitespace-nowrap"
                >
                  {transferring ? '…' : t('members.transferAction')}
                </button>
              </div>

              {transferError && <p className="text-xs text-error-red mt-2">{transferError}</p>}
              {transferSuccess && (
                <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
                  <CheckCircle2 size={13} />
                  {transferSuccess}
                </p>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
